import { useState } from "react";
import { CalendarClock, Layers, Globe, UserRound, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ClaudeBadge } from "./ClaudeMark";
import { heroBg } from "@/lib/images";
import { Picture } from "./Picture";
import { trackCtaClick } from "@/lib/analytics";

const VEED_EMBED_URL =
  "https://www.veed.io/embed/fbc84c90-6b07-41e3-8492-84eed34d7041?watermark=1&sharing=1&title=0";

const badges = [
  { icon: UserRound, label: "Individual" },
  { icon: Layers, label: "4 sessões de 1h30" },
  { icon: Globe, label: "Online · ao vivo" },
  { icon: CalendarClock, label: "Agenda combinada com você" },
];

export function HeroSection() {
  const [playing, setPlaying] = useState(false);

  return (
    <section
      id="top"
      className="relative isolate overflow-hidden pb-12 pt-24 md:pb-16 md:pt-28"
    >
      <span id="nav-scroll-sentinel" aria-hidden="true" className="pointer-events-none absolute left-0 top-6 h-px w-px" />
      <div className="absolute inset-0 -z-10">
        <Picture
          pic={heroBg}
          alt=""
          decorative
          sizes="100vw"
          loading="eager"
          fetchPriority="high"
          className="h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-midnight/85 via-midnight/95 to-midnight" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_color-mix(in_oklab,_var(--gold)_10%,_transparent)_0%,_transparent_55%)]" />
      </div>

      <div className="container-narrow relative z-10">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-6 inline-flex items-center gap-3 rounded-full border border-gold/40 bg-charcoal/90 px-4 py-1.5">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-gold" />
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold sm:text-[11px]">
              SPRINT CODE INDIVIDUAL · 4 sessões ao vivo
            </span>
          </div>

          <h1 className="text-balance text-[2rem] leading-[1.08] text-ivory sm:text-5xl md:text-6xl xl:text-7xl">
            Em 4 sessões, a sua operação mapeada, o Claude Code instalado e um time de agentes trabalhando por você.{" "}
            <span className="italic text-gold-soft">
              Construída com você, no seu computador.
            </span>
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-pretty text-base leading-relaxed text-slate-muted md:text-lg">
            Não é curso nem pacote pronto. É implantação sob medida, feita junto com você, no seu computador. Primeiro a gente mapeia a sua operação; depois instala o Claude Code e cria os agentes e as skills que o seu negócio precisa. Tudo numa arquitetura que é sua, e não de uma IA.
          </p>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-2.5 text-sm text-slate-muted">
            {badges.map((b) => (
              <span key={b.label} className="inline-flex min-w-0 items-center gap-2">
                <b.icon className="h-4 w-4 shrink-0 text-gold" /> {b.label}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-8 flex flex-col items-center gap-3">
          <Button
            asChild
            size="lg"
            className="h-14 w-full max-w-sm bg-gradient-gold px-10 text-base text-primary-foreground hover:opacity-90 sm:w-auto"
          >
            <a href="#inscricao" onClick={() => trackCtaClick("hero", "Quero meu Sprint Code")}>Quero meu Sprint Code</a>
          </Button>
          <a
            href="#sessoes"
            className="text-sm text-slate-muted underline underline-offset-4 transition-colors hover:text-gold-soft"
          >
            Prefiro ver as 4 sessões antes
          </a>
          <p className="max-w-md text-center text-xs text-slate-muted">
            Após o pagamento: conversa de 20 minutos para definir a agenda das
            suas sessões
          </p>
        </div>

        <div className="mx-auto mt-10 w-full max-w-[680px]">
          <div
            className="relative aspect-[3/2] w-full overflow-hidden rounded-2xl border border-gold/20 shadow-elegant"
          >
            {playing ? (
              <iframe
                src={`${VEED_EMBED_URL}&autoplay=1`}
                title="SPRINT CODE — Apresentação"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="absolute inset-0 h-full w-full"
              />
            ) : (
              <button
                type="button"
                onClick={() => setPlaying(true)}
                className="group absolute inset-0 h-full w-full"
              >
                <Picture
                  pic={heroBg}
                  alt=""
                  decorative
                  sizes="(min-width: 768px) 680px, calc(100vw - 48px)"
                  className="h-full w-full object-cover"
                />
                <span className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/60 to-midnight/20" />
                <span className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-gold shadow-elegant transition-transform duration-300 group-hover:scale-110">
                    <Play className="ml-0.5 h-6 w-6 text-primary-foreground" fill="currentColor" />
                  </span>
                  <span className="px-6 text-center text-sm text-ivory/90">
                    Assista: como o programa funciona na prática
                  </span>
                </span>
              </button>
            )}
          </div>
          <div className="mt-6 flex items-center justify-center gap-3">
            <span className="h-px w-8 bg-gold/30" />
            <ClaudeBadge />
            <span className="h-px w-8 bg-gold/30" />
          </div>
        </div>
      </div>
    </section>
  );
}
